import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'
import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient, type QueryKey } from '@tanstack/vue-query'
import { invalidateInvitations, workspaceInvitationKeys } from '@/apps/organizations/invitationKeys'
import {
  getAsset,
  listAssetAncestors,
  MAX_ANCESTRY_ASSETS,
  searchAssets,
  type AssetAncestorEntry,
  type AssetAncestryEntry,
  type AssetPage,
  type AssetSearchPage,
  type AssetView,
  type SearchAssetsRequest,
} from '@/api/assets/assets'
import { listAssetAudioTracks } from '@/api/assets/audioTracks'
import { listAssetChapters } from '@/api/assets/chapters'
import {
  getCollection,
  listCollectionItems,
  previewCollection,
  type PreviewCollectionRequest,
} from '@/api/assets/collections'
import {
  listMetadataSchemas,
  listMetadataValues,
  type MetadataSchemasQuery,
  type MetadataValuesQuery,
} from '@/api/assets/metadata'
import { getAssetProcessing, type AssetProcessingView } from '@/api/assets/processing'
import { getAssetRecommendations } from '@/api/assets/recommendations'
import { listAssetRelationships } from '@/api/assets/relationships'
import { listSavedSearches } from '@/api/assets/savedSearches'
import { listTags, type TagsQuery } from '@/api/assets/tags'
import { listAssetThumbnails } from '@/api/assets/thumbnails'
import type { OrgTrashCountQuery, OrgTrashPage, TrashCountQuery, TrashPage } from '@/api/assets/trash'
import { listAssetVersions } from '@/api/assets/versions'
import { getWorkspaceAssetSettings } from '@/api/assets/assetSettings'
import {
  listExportFields,
  listExportPresets,
  listOrgExportWorkspaces,
  listOrgExports,
  listWorkspaceExports,
  type ExportEntry,
  type OrganizationExportRequest,
} from '@/api/assets/exports'
import { getShare, getShareStatistics, listShareAssets, listShareEmails, type SharesQuery } from '@/api/assets/shares'
import {
  addWorkspaceMember,
  getWorkspace,
  getWorkspaceRole,
  listWorkspaceMembers,
  getWorkspaceTotals,
  listWorkspaces,
  type AddWorkspaceMemberRequest,
  type WorkspaceView,
} from '@/api/assets/workspaces'
import {
  cancelWorkspaceInvitation,
  createWorkspaceInvitation,
  listWorkspaceInvitations,
  type CreateWorkspaceInvitationRequest,
} from '@/api/assets/workspaceInvitations'
import { getOrgAssetSettings } from '@/api/orgs/assetSettings'
import { getStorageCosts } from '@/api/orgs/storageCosts'
import { getStorageUsage } from '@/api/orgs/storageUsage'
import type { WorkspacePermission } from '@/types/permissions'
import { holdsWorkspacePermission } from './composables/useAssetPermissions'
import { audioTrackPollDelay, preparingTracks } from './utils/audioTracks'

// No organization in any key below: the router clears the whole cache when the organization
// changes, so every entry already belongs to the one in the URL.

/** The workspace reads' keys. */
export const workspaceKeys = {
  all: ['workspaces'] as const,
  list: () => ['workspaces', 'list'] as const,
  /** What the list holds between them. Under `all`, so every write that moves a counter moves it. */
  totals: () => ['workspaces', 'totals'] as const,
  /** Every workspace of the organization, for its administrators. Under `all` for `totals`' reason. */
  overview: () => ['workspaces', 'overview'] as const,
  one: (workspaceId: string) => ['workspaces', 'one', workspaceId] as const,
}

/**
 * A workspace role's keys, by role key alone. The vocabulary is the organization's: a role declares
 * the same keys whichever workspace it is asked through, so N workspaces cost one read per
 * distinct role rather than one per workspace.
 */
export const workspaceRoleKeys = {
  all: ['workspace-roles'] as const,
  one: (roleKey: string) => ['workspace-roles', roleKey] as const,
}

/**
 * The library reads' keys. An asset's leaves — its versions, stills, tracks, chapters, edges and,
 * for a collection, its contents — sit under that asset's own key, so a write invalidating the
 * asset refreshes what hangs off it, and one invalidating `all` refreshes everything.
 */
export const assetKeys = {
  all: ['assets'] as const,
  searches: () => ['assets', 'search'] as const,
  search: (criteria: SearchAssetsRequest, page: AssetSearchPage) => ['assets', 'search', criteria, page] as const,
  one: (assetId: string) => ['assets', 'one', assetId] as const,
  /** Where one asset sits, in `workspaceId`'s tree or — `null` — the one it is read through. */
  ancestry: (assetId: string, workspaceId: string | null) => ['assets', 'ancestry', assetId, workspaceId] as const,
  /** The folders above a page of assets at once, for a picker's rows. */
  ancestries: (assetIds: readonly string[], workspaceId: string | null) =>
    ['assets', 'ancestries', workspaceId, assetIds] as const,
  processing: (assetId: string) => ['assets', 'one', assetId, 'processing'] as const,
  versions: (assetId: string) => ['assets', 'one', assetId, 'versions'] as const,
  thumbnails: (assetId: string) => ['assets', 'one', assetId, 'thumbnails'] as const,
  audioTracks: (assetId: string) => ['assets', 'one', assetId, 'audio-tracks'] as const,
  chapters: (assetId: string) => ['assets', 'one', assetId, 'chapters'] as const,
  relationships: (assetId: string) => ['assets', 'one', assetId, 'relationships'] as const,
  recommendations: (assetId: string) => ['assets', 'one', assetId, 'recommendations'] as const,
  collection: (collectionId: string) => ['assets', 'one', collectionId, 'collection'] as const,
  collectionItems: (collectionId: string) => ['assets', 'one', collectionId, 'collection-items'] as const,
  collectionPreview: (collectionId: string, request: PreviewCollectionRequest) =>
    ['assets', 'one', collectionId, 'collection-preview', request] as const,
  /** The organization's storage usage. Under `all` so that every write that moves the library moves it. */
  storageUsage: () => ['assets', 'storage-usage'] as const,
  /** What that storage costs a month, by category. Under `all` for the same reason. */
  storageCosts: () => ['assets', 'storage-costs'] as const,
}

/** The trashes, a workspace's and the organization's. A deletion adds to them and a restore takes out of them. */
export const trashKeys = {
  all: ['trash'] as const,
  workspace: (workspaceId: string, page: TrashPage) => ['trash', workspaceId, page] as const,
  /** Under the same prefix as the listing, so every write that invalidates one invalidates both. */
  count: (workspaceId: string, query: TrashCountQuery) => ['trash', workspaceId, 'count', query] as const,
  /** The organization's, beside the workspaces' — a workspace uid never reads `organization`. */
  organization: (page: OrgTrashPage) => ['trash', 'organization', page] as const,
  organizationCount: (query: OrgTrashCountQuery) => ['trash', 'organization', 'count', query] as const,
}

/** The tag vocabulary: a workspace's own and the organization's system tags. */
export const tagKeys = {
  all: ['tags'] as const,
  list: (query: TagsQuery) => ['tags', query] as const,
}

/** The caller's saved searches, organization-wide or in one workspace. */
export const savedSearchKeys = {
  all: ['saved-searches'] as const,
  list: (workspaceId: string | null) => ['saved-searches', workspaceId] as const,
}

/** The custom-field reads' keys. */
export const metadataKeys = {
  all: ['metadata'] as const,
  schemas: (query: MetadataSchemasQuery) => ['metadata', 'schemas', query] as const,
  values: (query: MetadataValuesQuery) => ['metadata', 'values', query] as const,
}

/**
 * The public links' keys. What hangs off one link sits under its own key, so a write on the link
 * invalidates `one(shareId)`; one that makes or removes a link invalidates `lists()` too.
 */
export const shareKeys = {
  all: ['shares'] as const,
  lists: () => ['shares', 'list'] as const,
  list: (query: SharesQuery) => ['shares', 'list', query] as const,
  /** Every page of the links `query` selects, walked to the end. */
  whole: (query: SharesQuery) => ['shares', 'list', 'whole', query] as const,
  one: (shareId: string) => ['shares', 'one', shareId] as const,
  assets: (shareId: string) => ['shares', 'one', shareId, 'assets'] as const,
  emails: (shareId: string) => ['shares', 'one', shareId, 'emails'] as const,
  statistics: (shareId: string) => ['shares', 'one', shareId, 'statistics'] as const,
}

/**
 * The export's column catalogue and saved configurations, by the workspace asked through — `null`
 * for the organization alone. A workspace's preset list carries the organization's presets too, so a
 * preset write invalidates `presets()` whole. The logs share one prefix: an export, whichever its
 * scope, is a row of the organization's log and maybe of a workspace's.
 */
export const exportKeys = {
  all: ['exports'] as const,
  fields: (workspaceId: string | null) => ['exports', 'fields', workspaceId] as const,
  count: (workspaceId: string | null, criteria: SearchAssetsRequest) =>
    ['exports', 'count', workspaceId, criteria] as const,
  orgCount: (request: OrganizationExportRequest) => ['exports', 'org-count', request] as const,
  orgWorkspaces: () => ['exports', 'org-workspaces'] as const,
  presets: () => ['exports', 'presets'] as const,
  presetList: (workspaceId: string | null) => ['exports', 'presets', workspaceId] as const,
  logs: () => ['exports', 'logs'] as const,
  workspaceLog: (workspaceId: string) => ['exports', 'logs', 'workspace', workspaceId] as const,
  orgLog: () => ['exports', 'logs', 'organization'] as const,
}

/** Who sits in one workspace. A seat write also moves the roles' `member_count` (`workspaceRoleKeys`). */
export const workspaceMemberKeys = {
  all: ['workspace-members'] as const,
  list: (workspaceId: string) => ['workspace-members', workspaceId] as const,
}

/**
 * The settings pair. Every workspace inherits the organization's level, so a write there
 * invalidates `all`; a workspace's own write invalidates that workspace alone.
 */
export const assetSettingsKeys = {
  all: ['asset-settings'] as const,
  org: () => ['asset-settings', 'org'] as const,
  workspace: (workspaceId: string) => ['asset-settings', 'workspace', workspaceId] as const,
}

// ------------------------------------------------------------------------------ workspaces ----

/** Every workspace the caller holds a seat in, each with the role they hold there. */
export function useWorkspaces(options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
  return useQuery({
    queryKey: workspaceKeys.list(),
    queryFn: ({ signal }) => listWorkspaces(signal),
    enabled: computed(() => toValue(options.enabled) !== false),
  })
}

/**
 * What the workspaces `useWorkspaces` lists hold between them, each asset once — never the sum of
 * their counters, which counts an asset in the shared pool twice.
 */
export function useWorkspaceTotals(options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
  return useQuery({
    queryKey: workspaceKeys.totals(),
    queryFn: ({ signal }) => getWorkspaceTotals(signal),
    enabled: computed(() => toValue(options.enabled) !== false),
  })
}

/**
 * One workspace, with the role the caller holds in it. Idle while there is no id; the list's copy
 * stands in while the read is under way, so a screen opened from the drawer has its name at once.
 */
export function useWorkspace(workspaceId: MaybeRefOrGetter<string | null | undefined>) {
  const queryClient = useQueryClient()
  const id = computed(() => toValue(workspaceId) ?? '')
  return useQuery({
    queryKey: computed(() => workspaceKeys.one(id.value)),
    queryFn: ({ signal }) => getWorkspace(id.value, signal),
    enabled: computed(() => id.value !== ''),
    placeholderData: () =>
      queryClient.getQueryData<WorkspaceView[]>(workspaceKeys.list())?.find((workspace) => workspace.uid === id.value),
  })
}

type RoleHolder = Pick<WorkspaceView, 'uid' | 'role'>

export interface UseWorkspaceRoleReturn {
  /** The workspace-scoped keys the role declares; empty until it is read. */
  permissions: ComputedRef<readonly string[]>
  /** Whether that role holds `permission`. */
  can: (permission: WorkspacePermission) => boolean
  isLoading: ComputedRef<boolean>
  /** The role's read failed: `can` answering `false` then says nothing about the role. */
  isError: ComputedRef<boolean>
}

/**
 * What the role the caller holds in one workspace declares. Asked through that workspace and cached
 * by role key, so every list row holding the same role shares the one read.
 *
 * **A workspace read without a seat has no role** — a public one, or any under
 * `tenancy.workspaces.view_all` — so there is nothing to read, and it holds no key.
 */
export function useWorkspaceRole(workspace: MaybeRefOrGetter<RoleHolder | null | undefined>): UseWorkspaceRoleReturn {
  const holder = computed(() => toValue(workspace) ?? null)
  const role = computed(() => holder.value?.role ?? null)
  const query = useQuery({
    queryKey: computed(() => workspaceRoleKeys.one(role.value ?? '')),
    queryFn: ({ signal }) => getWorkspaceRole(holder.value?.uid ?? '', role.value ?? '', signal),
    enabled: computed(() => role.value !== null),
  })
  const permissions = computed<readonly string[]>(() => query.data.value?.permissions ?? [])
  return {
    permissions,
    can: (permission) => holdsWorkspacePermission(permissions.value, permission),
    // Loading from the moment there is a role to read until it is read or refused — not only
    // while the request is in flight, so a guard never decides in the tick before it starts.
    isLoading: computed(() => role.value !== null && query.data.value === undefined && !query.isError.value),
    isError: computed(() => query.isError.value),
  }
}

export interface UseWorkspaceRolesReturn {
  /** Whether the role the caller holds in `workspace` declares `permission`. */
  can: (workspace: RoleHolder | null | undefined, permission: WorkspacePermission) => boolean
  isLoading: ComputedRef<boolean>
}

/**
 * What the caller may do in each of several workspaces — one role read per distinct role key, in
 * the cache `useWorkspaceRole` reads too. Nothing is held in a workspace whose role is not read yet.
 */
export function useWorkspaceRoles(
  workspaces: MaybeRefOrGetter<readonly RoleHolder[] | null | undefined>,
): UseWorkspaceRolesReturn {
  // One workspace to ask each role through: any holder of it answers the same keys.
  const askedThrough = computed(() => {
    const byRole = new Map<string, string>()
    for (const workspace of toValue(workspaces) ?? []) {
      // A workspace read without a seat has no role to ask for.
      if (workspace.role && !byRole.has(workspace.role)) byRole.set(workspace.role, workspace.uid)
    }
    return [...byRole]
  })

  const roles = useQueries({
    queries: computed(() =>
      askedThrough.value.map(([roleKey, workspaceId]) => ({
        queryKey: workspaceRoleKeys.one(roleKey),
        queryFn: ({ signal }: { signal: AbortSignal }) => getWorkspaceRole(workspaceId, roleKey, signal),
      })),
    ),
  })

  const permissionsByRole = computed(() => {
    const held = new Map<string, readonly string[]>()
    for (const result of roles.value) {
      if (result.data) held.set(result.data.key, result.data.permissions)
    }
    return held
  })

  return {
    can: (workspace, permission) =>
      !!workspace?.role && holdsWorkspacePermission(permissionsByRole.value.get(workspace.role), permission),
    isLoading: computed(() => roles.value.some((result) => result.isLoading)),
  }
}

export interface UseWorkspacePermissionsReturn {
  /** The workspace, or `null` until it is read. */
  workspace: ComputedRef<WorkspaceView | null>
  /** The role the caller holds in the workspace, or `null` until it is read. */
  role: ComputedRef<string | null>
  /** The workspace-scoped keys that role declares. */
  permissions: ComputedRef<readonly string[]>
  /** Whether that role holds `permission`. */
  can: (permission: WorkspacePermission) => boolean
  isLoading: ComputedRef<boolean>
  /** The workspace's or the role's read failed: `can` answering `false` then says nothing. */
  isError: ComputedRef<boolean>
}

/**
 * What the caller may do inside one workspace: the keys the role they hold there declares.
 *
 * Two reads, because that is how the backend answers it — `workspace_read` names the caller's
 * role, `workspaces_role_read` what that role holds. **Never the organization's set**: a
 * workspace key is not in it, so a gate reading it there would refuse everybody. Nothing is held
 * while either read is pending or refused.
 */
export function useWorkspacePermissions(
  workspaceId: MaybeRefOrGetter<string | null | undefined>,
): UseWorkspacePermissionsReturn {
  const query = useWorkspace(workspaceId)
  const workspace = computed(() => query.data.value ?? null)
  const role = useWorkspaceRole(workspace)

  return {
    workspace,
    role: computed(() => workspace.value?.role ?? null),
    permissions: role.permissions,
    can: role.can,
    isLoading: computed(() => query.isLoading.value || role.isLoading.value),
    isError: computed(() => query.isError.value || role.isError.value),
  }
}

// ---------------------------------------------------------------------------------- assets ----

/** Phases in which an asset is still changing under the reader, and worth asking about again. */
const CHANGING_STATUSES: ReadonlySet<string> = new Set(['uploading', 'processing'])

/** How often an asset still in its ingest is asked about again. */
const PROCESSING_POLL_MS = 5000

/** Whether a phase is one the asset is still moving out of. */
export function isStatusChanging(status: string | null | undefined): boolean {
  return !!status && CHANGING_STATUSES.has(status)
}

/**
 * Whether an asset is still in its ingest, and a read of it will soon be stale.
 *
 * **This is the phase and not the pipeline.** Since the queue marks an asset `ready` at the first
 * set a player can start on, `ready` no longer means the work is over: the ladder and every
 * derivative carry on behind it. What is still owed is {@link isProcessingLive} over the
 * processing read, and the two answer different questions on purpose.
 */
export function isAssetChanging(asset: Pick<AssetView, 'status'> | null | undefined): boolean {
  return isStatusChanging(asset?.status)
}

/** One asset, asked again every few seconds while its ingest is under way. Idle without an id. */
export function useAsset(assetId: MaybeRefOrGetter<string | null | undefined>) {
  const id = computed(() => toValue(assetId) ?? '')
  return useQuery({
    queryKey: computed(() => assetKeys.one(id.value)),
    queryFn: ({ signal }) => getAsset(id.value, signal),
    enabled: computed(() => id.value !== ''),
    refetchInterval: (query) => (isAssetChanging(query.state.data) ? PROCESSING_POLL_MS : false),
  })
}

/**
 * Whether the media queue still holds work for an asset.
 *
 * **`progress.live` and not `step`**, which are two scopes: `step` is reduced over the current
 * run's live rows, while this count is every live job of the asset whatever generation it belongs
 * to — a rung that outlived a version bump is work that is still going to happen and has no word.
 * So a screen showing `step` may show nothing while this is still true, and that is the right way
 * round: the word is what is happening, this is whether to ask again.
 */
export function isProcessingLive(processing: AssetProcessingView | null | undefined): boolean {
  return (processing?.progress.live ?? 0) > 0
}

/**
 * What the media queue owes one asset: every job, the counts, and the one word for what is
 * happening now. Idle without an id.
 *
 * **It is asked again for two reasons, and neither covers the other.** `status` covers the gap
 * before any job exists — an asset still `uploading` has no `probe` row, so this read's own counts
 * would stop the poll before the pipeline started. Past `ready` it is this read that keeps it
 * going, the ladder and the derivatives outliving the phase.
 */
export function useAssetProcessing(
  assetId: MaybeRefOrGetter<string | null | undefined>,
  status: MaybeRefOrGetter<string | null | undefined>,
) {
  const id = computed(() => toValue(assetId) ?? '')
  return useQuery({
    queryKey: computed(() => assetKeys.processing(id.value)),
    queryFn: ({ signal }) => getAssetProcessing(id.value, signal),
    enabled: computed(() => id.value !== ''),
    refetchInterval: (query) =>
      isStatusChanging(toValue(status)) || isProcessingLive(query.state.data) ? PROCESSING_POLL_MS : false,
  })
}

/**
 * Where one asset sits: the folders above it root first, the tree they belong to, and its own name
 * — one `assets_ancestors_list` call whatever the depth. `null` for an asset the caller cannot read,
 * or that `workspaceId` does not hold. Without a workspace the tree is the one `asset_read` answers
 * through. Idle without an id.
 */
export function useAssetAncestry(
  assetId: MaybeRefOrGetter<string | null | undefined>,
  workspaceId: MaybeRefOrGetter<string | null | undefined> = null,
) {
  return useQuery(ancestryOptions(assetId, workspaceId, (entry) => entry))
}

/** A folder's breadcrumb, as {@link useFolderPath} answers it. */
export interface FolderPath {
  /** The workspace whose tree the path is. */
  workspaceId: string
  /** From the root down, **the folder itself last**. */
  folders: AssetAncestorEntry[]
  /**
   * Whether `folders` starts at the workspace's root. `false` when a folder above is one the caller
   * may not see: a breadcrumb draws an ellipsis before the first entry.
   */
  reachesRoot: boolean
}

/** The breadcrumb an ancestry answer draws when the asset it is about is the folder being shown. */
export function folderPathOf(entry: AssetAncestryEntry): FolderPath {
  return {
    workspaceId: entry.workspace_id,
    folders: [...entry.folders, { uid: entry.asset_id, name: entry.name }],
    reachesRoot: entry.reaches_root,
  }
}

/**
 * A folder and the folders above it, root first, the folder itself last — the ancestry read under
 * the same key as {@link useAssetAncestry}, so a folder's breadcrumb and its detail page share one
 * request. `null` for a folder the caller cannot read.
 */
export function useFolderPath(
  folderId: MaybeRefOrGetter<string | null | undefined>,
  workspaceId: MaybeRefOrGetter<string | null | undefined> = null,
) {
  return useQuery(ancestryOptions(folderId, workspaceId, (entry) => (entry ? folderPathOf(entry) : null)))
}

/** One folder of a level, as {@link useFolderLevel} answers it. */
export interface FolderLevelEntry {
  uid: string
  name: string
  /** Everything filed under it, subfolder or not — `child_count` counts every placement. */
  childCount: number
  /** The folders among them: whether the folder unfolds into a level of its own. */
  subfolderCount: number
}

/** One level of a tree at a time: the page cap covers it. */
const FOLDER_LEVEL_PAGE: AssetSearchPage = { limit: 200 }

// Alphabetical, as a tree reads: the search walks by creation date.
function folderLevelOf(page: AssetPage): FolderLevelEntry[] {
  return page.data
    .map((item) => ({
      uid: item.uid,
      name: item.name,
      childCount: item.child_count ?? 0,
      subfolderCount: item.subfolder_count ?? 0,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * The folders directly under `parentId` in `workspaceId`'s tree — under its root for `null` —
 * sorted by name. One key for every screen that browses a tree a level at a time (the move
 * dialog's folder browser, the drawer's folder tree), so a level either has read is the other's
 * too. Idle without a workspace.
 */
export function useFolderLevel(
  workspaceId: MaybeRefOrGetter<string | null | undefined>,
  parentId: MaybeRefOrGetter<string | null>,
) {
  const criteria = computed<SearchAssetsRequest | null>(() => {
    const workspace = toValue(workspaceId)
    if (!workspace) return null
    const parent = toValue(parentId)
    return {
      workspace_ids: [workspace],
      types: ['folder'],
      parent:
        parent === null ? { workspace_id: workspace, is_root: true } : { workspace_id: workspace, parent_id: parent },
    }
  })
  return useQuery({
    queryKey: computed(() => assetKeys.search(criteria.value ?? {}, FOLDER_LEVEL_PAGE)),
    queryFn: ({ queryKey: [, , criteria, page], signal }) => searchAssets(criteria, page, signal),
    enabled: computed(() => criteria.value !== null),
    select: folderLevelOf,
  })
}

/**
 * The folders above each of a page of assets, in one call, by asset uid — what a picker shows under
 * each row to tell two folders of one name apart. Only the first {@link MAX_ANCESTRY_ASSETS} are
 * asked about. Idle while none is named.
 */
export function useAssetAncestries(
  assetIds: MaybeRefOrGetter<readonly string[]>,
  workspaceId: MaybeRefOrGetter<string | null | undefined> = null,
) {
  const ids = computed(() => toValue(assetIds).slice(0, MAX_ANCESTRY_ASSETS))
  const tree = computed(() => toValue(workspaceId) ?? null)
  return useQuery({
    queryKey: computed(() => assetKeys.ancestries(ids.value, tree.value)),
    queryFn: async ({ signal }) =>
      new Map((await listAssetAncestors(ids.value, tree.value, signal)).map((entry) => [entry.asset_id, entry])),
    enabled: computed(() => ids.value.length > 0),
    placeholderData: (previous) => previous,
  })
}

function ancestryOptions<T>(
  assetId: MaybeRefOrGetter<string | null | undefined>,
  workspaceId: MaybeRefOrGetter<string | null | undefined>,
  select: (entry: AssetAncestryEntry | null) => T,
) {
  const id = computed(() => toValue(assetId) ?? '')
  const tree = computed(() => toValue(workspaceId) ?? null)
  return {
    queryKey: computed(() => assetKeys.ancestry(id.value, tree.value)),
    queryFn: async ({ signal }: { signal: AbortSignal }) =>
      (await listAssetAncestors([id.value], tree.value, signal))[0] ?? null,
    enabled: computed(() => id.value !== ''),
    select,
    // A breadcrumb keeps the path it shows while the next one loads, rather than blinking — and
    // shows none once no asset is named.
    placeholderData: (previous: AssetAncestryEntry | null | undefined) => (id.value !== '' ? previous : undefined),
  }
}

// ---------------------------------------------------------------------------- asset leaves ----

/**
 * One read hanging off an asset, under that asset's key. Idle without an id. The key is typed as
 * vue-query's own: a generic tuple in its place matches none of `useQuery`'s overloads, and the
 * answer comes out untyped.
 */
function useAssetLeaf<T>(
  assetId: MaybeRefOrGetter<string | null | undefined>,
  key: (assetId: string) => QueryKey,
  read: (assetId: string, signal: AbortSignal) => Promise<T>,
) {
  const id = computed(() => toValue(assetId) ?? '')
  return useQuery({
    queryKey: computed(() => key(id.value)),
    queryFn: ({ signal }) => read(id.value, signal),
    enabled: computed(() => id.value !== ''),
  })
}

/** Every generation of an asset, and which one it is at. */
export function useAssetVersionList(assetId: MaybeRefOrGetter<string | null | undefined>) {
  return useAssetLeaf(assetId, assetKeys.versions, listAssetVersions)
}

/** An asset's stills at its current generation. */
export function useAssetThumbnailList(assetId: MaybeRefOrGetter<string | null | undefined>) {
  return useAssetLeaf(assetId, assetKeys.thumbnails, listAssetThumbnails)
}

/**
 * A video's sound tracks, asked again while one is being prepared for the player — soon after a
 * change, then less and less often ({@link audioTrackPollDelay}): an upload's encode lands in
 * minutes, a language of the source queued behind other work can take hours, and a page left open
 * on it must not ask every few seconds all that time. A track that starts or stops being prepared
 * starts the wait over. Idle without an id.
 *
 * Nothing is asked while the tab is hidden — an interval's read is skipped then
 * (`refetchIntervalInBackground` is off) — and the list is read again on the way back.
 */
export function useAssetAudioTrackList(assetId: MaybeRefOrGetter<string | null | undefined>) {
  const id = computed(() => toValue(assetId) ?? '')
  // Where the current wait is counted from: the query, the tracks being prepared, its reads then.
  // The interval is asked for on every state change of the query, a read starting included, so the
  // reads are counted off the query's own `dataUpdateCount`, never off the calls.
  let since: { queryHash: string; preparing: string; reads: number } | null = null
  return useQuery({
    queryKey: computed(() => assetKeys.audioTracks(id.value)),
    queryFn: ({ signal }) => listAssetAudioTracks(id.value, signal),
    enabled: computed(() => id.value !== ''),
    refetchInterval: (query) => {
      const preparing = preparingTracks(query.state.data)
      if (!preparing) {
        since = null
        return false
      }
      if (!since || since.queryHash !== query.queryHash || since.preparing !== preparing) {
        since = { queryHash: query.queryHash, preparing, reads: query.state.dataUpdateCount }
      }
      return audioTrackPollDelay(query.state.dataUpdateCount - since.reads)
    },
    refetchOnWindowFocus: (query) => preparingTracks(query.state.data) !== '',
  })
}

/** An asset's chapters, in timeline order. */
export function useAssetChapterList(assetId: MaybeRefOrGetter<string | null | undefined>) {
  return useAssetLeaf(assetId, assetKeys.chapters, listAssetChapters)
}

/** Every edge touching an asset, from both ends. */
export function useAssetRelationshipList(assetId: MaybeRefOrGetter<string | null | undefined>) {
  return useAssetLeaf(assetId, assetKeys.relationships, listAssetRelationships)
}

/** A video's recommendations strip: its heading and its videos, in order. */
export function useAssetRecommendations(assetId: MaybeRefOrGetter<string | null | undefined>) {
  return useAssetLeaf(assetId, assetKeys.recommendations, getAssetRecommendations)
}

/** One collection, whole: the asset's record and the list's columns. */
export function useCollection(collectionId: MaybeRefOrGetter<string | null | undefined>) {
  return useAssetLeaf(collectionId, assetKeys.collection, getCollection)
}

/** What is in a collection: a curated one's entries, or what a smart one's rules select now. */
export function useCollectionItemList(collectionId: MaybeRefOrGetter<string | null | undefined>) {
  return useAssetLeaf(collectionId, assetKeys.collectionItems, listCollectionItems)
}

/**
 * What a set of rules would select, without storing them. Keeps the previous answer up while the
 * next one loads, so a form edited field by field does not blink.
 */
export function useCollectionPreview(
  collectionId: MaybeRefOrGetter<string | null | undefined>,
  request: MaybeRefOrGetter<PreviewCollectionRequest>,
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  const id = computed(() => toValue(collectionId) ?? '')
  return useQuery({
    queryKey: computed(() => assetKeys.collectionPreview(id.value, toValue(request))),
    queryFn: ({ signal }) => previewCollection(id.value, toValue(request), signal),
    enabled: computed(() => id.value !== '' && toValue(options.enabled) !== false),
    placeholderData: (previous) => previous,
  })
}

// -------------------------------------------------------------------------------- tags ----

/** The tags a picker offers for `query`, keeping the last answer up while the next search loads. */
export function useTagList(
  query: MaybeRefOrGetter<TagsQuery> = {},
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  return useQuery({
    queryKey: computed(() => tagKeys.list(toValue(query))),
    queryFn: ({ signal }) => listTags(toValue(query), signal),
    enabled: computed(() => toValue(options.enabled) !== false),
    placeholderData: (previous) => previous,
  })
}

// ------------------------------------------------------------------------ saved searches ----

/**
 * The caller's saved searches in one workspace, or organization-wide without one, each with what
 * it matches today.
 */
export function useSavedSearchList(
  workspaceId: MaybeRefOrGetter<string | null | undefined>,
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  const id = computed(() => toValue(workspaceId) ?? null)
  return useQuery({
    queryKey: computed(() => savedSearchKeys.list(id.value)),
    queryFn: ({ signal }) => listSavedSearches(id.value ? { workspace_id: id.value } : {}, signal),
    enabled: computed(() => toValue(options.enabled) !== false),
  })
}

// -------------------------------------------------------------------------------- metadata ----

/** The custom-field schemas that apply to what `query` names. */
export function useMetadataSchemas(
  query: MaybeRefOrGetter<MetadataSchemasQuery> = {},
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  return useQuery({
    queryKey: computed(() => metadataKeys.schemas(toValue(query))),
    queryFn: ({ signal }) => listMetadataSchemas(toValue(query), signal),
    enabled: computed(() => toValue(options.enabled) !== false),
    // Schemas are an administrator's configuration: they change rarely, and never under a reader.
    staleTime: 5 * 60 * 1000,
  })
}

/** The custom-field values of the rows `query.ids` names. Idle while it names none. */
export function useMetadataValues(query: MaybeRefOrGetter<MetadataValuesQuery>) {
  return useQuery({
    queryKey: computed(() => metadataKeys.values(toValue(query))),
    queryFn: ({ signal }) => listMetadataValues(toValue(query), signal),
    enabled: computed(() => !!toValue(query).ids),
  })
}

// ---------------------------------------------------------------------------------- shares ----

/** One read hanging off a link, under that link's key. Idle without an id. */
function useShareLeaf<T>(
  shareId: MaybeRefOrGetter<string | null | undefined>,
  key: (shareId: string) => QueryKey,
  read: (shareId: string, signal: AbortSignal) => Promise<T>,
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  const id = computed(() => toValue(shareId) ?? '')
  return useQuery({
    queryKey: computed(() => key(id.value)),
    queryFn: ({ signal }) => read(id.value, signal),
    enabled: computed(() => id.value !== '' && toValue(options.enabled) !== false),
  })
}

/** One link and its counters. */
export function useShare(shareId: MaybeRefOrGetter<string | null | undefined>) {
  return useShareLeaf(shareId, shareKeys.one, getShare)
}

/** What a link names, in the order a visitor is shown them. */
export function useShareAssetList(shareId: MaybeRefOrGetter<string | null | undefined>) {
  return useShareLeaf(shareId, shareKeys.assets, listShareAssets)
}

/**
 * The batches a link was emailed in. Refused without `assets.asset.share` — the answer carries every
 * recipient's personal link — so gate `enabled` on it.
 */
export function useShareEmailList(
  shareId: MaybeRefOrGetter<string | null | undefined>,
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  return useShareLeaf(shareId, shareKeys.emails, listShareEmails, options)
}

/** What a link has done. Refused without `assets.asset.view_analytics`, so gate `enabled` on it. */
export function useShareStatistics(
  shareId: MaybeRefOrGetter<string | null | undefined>,
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  return useShareLeaf(shareId, shareKeys.statistics, getShareStatistics, options)
}

// --------------------------------------------------------------------------------- exports ----

/**
 * The columns an export can carry, with `workspaceId`'s own custom fields where one is named. An
 * empty id — an optional route param the URL left out — is the organization alone.
 */
export function useExportFields(workspaceId: MaybeRefOrGetter<string | null | undefined>) {
  const id = computed(() => toValue(workspaceId) || null)
  return useQuery({
    queryKey: computed(() => exportKeys.fields(id.value)),
    queryFn: ({ signal }) => listExportFields(id.value ? { workspace_id: id.value } : {}, signal),
    // The catalogue is an administrator's configuration, as the metadata schemas are.
    staleTime: 5 * 60 * 1000,
  })
}

/** The saved export configurations: the organization's, and `workspaceId`'s own where one is named. */
export function useExportPresets(workspaceId: MaybeRefOrGetter<string | null | undefined>) {
  const id = computed(() => toValue(workspaceId) || null)
  return useQuery({
    queryKey: computed(() => exportKeys.presetList(id.value)),
    queryFn: ({ signal }) => listExportPresets(id.value ? { workspace_id: id.value } : {}, signal),
  })
}

/**
 * Every workspace the organization's export can be narrowed to, seated or not — its own read,
 * behind `assets.library.export`, since `useWorkspaces` lists only the caller's seats. Gate
 * `enabled` on the organization's form being shown.
 */
export function useExportWorkspaces(options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
  return useQuery({
    queryKey: exportKeys.orgWorkspaces(),
    queryFn: ({ signal }) => listOrgExportWorkspaces(signal),
    enabled: computed(() => toValue(options.enabled) !== false),
  })
}

/** How many exports one page of a log carries. */
const EXPORT_LOG_PAGE_SIZE = 20

/** How often a log is asked again while one of its exports is still on its way. */
const EXPORT_LOG_POLL_MS = 5000

/**
 * How long a synchronous export's `running` is believed. Its row is finished when the response is
 * read to its end, and one whose process died mid-stream reads `running` until `export-sync-expiry`
 * fails it an hour later (`docs/plans/organization-exports.md` X1) — so only a recent one is worth
 * polling for; a live stream ends in seconds.
 */
const SYNC_EXPORT_SETTLE_MS = 2 * 60 * 1000

/** Whether an export's row is still expected to move: queued, or being written. */
export function isExportSettling(entry: ExportEntry, now = Date.now()): boolean {
  if (entry.status !== 'queued' && entry.status !== 'running') return false
  return entry.mode === 'async' || now - Date.parse(entry.created_at) < SYNC_EXPORT_SETTLE_MS
}

/**
 * An export log, newest first, a page at a time: `workspaceId`'s, or — for `'organization'` — the
 * organization's whole log. Idle on `null` or while `enabled` is false. Asked again every few
 * seconds while a loaded export is still queued or being written, so a status converges without a
 * reload.
 */
export function useExportLog(
  source: MaybeRefOrGetter<string | 'organization' | null>,
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  const scope = computed(() => toValue(source) || null)
  const query = useInfiniteQuery({
    // No workspace yet is a key of its own, never fetched: sharing the organization's would show
    // its cached rows on a page that has not said which log it reads.
    queryKey: computed(() =>
      scope.value === 'organization' ? exportKeys.orgLog() : exportKeys.workspaceLog(scope.value ?? ''),
    ),
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) => {
      const walk = { order: 'desc' as const, limit: EXPORT_LOG_PAGE_SIZE, after: pageParam || undefined }
      return scope.value === 'organization'
        ? listOrgExports(walk, signal)
        : listWorkspaceExports(scope.value ?? '', walk, signal)
    },
    getNextPageParam: (last) => last.next ?? undefined,
    enabled: computed(() => scope.value !== null && toValue(options.enabled) !== false),
    refetchInterval: (current) => {
      const now = Date.now()
      const settling = (current.state.data?.pages ?? []).some((page) =>
        page.data.some((entry) => isExportSettling(entry, now)),
      )
      return settling ? EXPORT_LOG_POLL_MS : false
    },
  })

  const entries = computed<ExportEntry[]>(() => (query.data.value?.pages ?? []).flatMap((page) => page.data))

  return {
    entries,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  }
}

// ----------------------------------------------------------------------- workspace seats ----

/** Who sits in one workspace, derived seats included (`is_implicit`). Idle without an id. */
export function useWorkspaceMemberList(workspaceId: MaybeRefOrGetter<string | null | undefined>) {
  const id = computed(() => toValue(workspaceId) ?? '')
  return useQuery({
    queryKey: computed(() => workspaceMemberKeys.list(id.value)),
    queryFn: ({ signal }) => listWorkspaceMembers(id.value, {}, signal),
    enabled: computed(() => id.value !== ''),
  })
}

/**
 * Seat somebody already in the organization. A seat moves this workspace's list and the
 * `member_count` of every role, which is counted across the organization.
 */
export function useAddWorkspaceMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ workspaceId, ...request }: { workspaceId: string } & AddWorkspaceMemberRequest) =>
      addWorkspaceMember(workspaceId, request),
    onSuccess: (_seat, { workspaceId }) => {
      void queryClient.invalidateQueries({ queryKey: workspaceMemberKeys.list(workspaceId) })
      void queryClient.invalidateQueries({ queryKey: workspaceRoleKeys.all })
    },
  })
}

// ----------------------------------------------------------------- workspace invitations ----

/**
 * What one workspace is still waiting on, newest first. Reading it needs `assets.members.invite`
 * there, so a caller without the key should leave it idle rather than collect a 403: pass
 * `enabled`. Idle without an id.
 */
export function useWorkspaceInvitationList(
  workspaceId: MaybeRefOrGetter<string | null | undefined>,
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  const id = computed(() => toValue(workspaceId) ?? '')
  return useQuery({
    queryKey: computed(() => workspaceInvitationKeys.list(id.value)),
    queryFn: ({ signal }) => listWorkspaceInvitations(id.value, signal),
    enabled: computed(() => id.value !== '' && toValue(options.enabled) !== false),
  })
}

/**
 * Offer an address a seat under a role, re-offer one, or send one again (`supersedes`, and no
 * `role`): the same call, the same refusals.
 *
 * **It invalidates every pending list**, the organization's and each workspace's — see
 * `workspaceInvitationKeys` — **on a refusal too**: a re-send refused `404` or `409` is the list
 * having moved since it was drawn — the invitation accepted, withdrawn or lapsed, the address a
 * member since — and a stale row would only be refused again. It moves no seat list: nobody is
 * seated until the invitee accepts, which happens on `base-auth`'s own accept page and not in this
 * tab.
 */
export function useCreateWorkspaceInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ workspaceId, ...body }: { workspaceId: string } & CreateWorkspaceInvitationRequest) =>
      createWorkspaceInvitation(workspaceId, body),
    onSettled: () => {
      void invalidateInvitations(queryClient)
    },
  })
}

/**
 * Withdraw an outstanding invitation. **The whole offer goes, not this workspace's seat in it**, so
 * an invitee named on two workspaces leaves both lists, and the organization's — which is why this
 * invalidates every pending list too, and on a refusal as well: its `404` is a row that was no
 * longer pending when it was asked about.
 * No seat list moves: a withdrawn offer was never a seat.
 */
export function useCancelWorkspaceInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ workspaceId, invitationId }: { workspaceId: string; invitationId: string }) =>
      cancelWorkspaceInvitation(workspaceId, invitationId),
    onSettled: () => {
      void invalidateInvitations(queryClient)
    },
  })
}

// ---------------------------------------------------------------------------- settings pair ----

/**
 * One workspace's settings: `effective` is what to render, `overrides` what drives each field's
 * inherited or overridden state. Readable with a seat; idle without an id.
 */
export function useWorkspaceAssetSettings(workspaceId: MaybeRefOrGetter<string | null | undefined>) {
  const id = computed(() => toValue(workspaceId) ?? '')
  return useQuery({
    queryKey: computed(() => assetSettingsKeys.workspace(id.value)),
    queryFn: ({ signal }) => getWorkspaceAssetSettings(id.value, signal),
    enabled: computed(() => id.value !== ''),
  })
}

/** The organization's settings. Open to every member. */
export function useOrgAssetSettings(options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
  return useQuery({
    queryKey: assetSettingsKeys.org(),
    queryFn: ({ signal }) => getOrgAssetSettings(signal),
    enabled: computed(() => toValue(options.enabled) !== false),
  })
}

// --------------------------------------------------------------------------- storage usage ----

/**
 * What the organization's library holds, per kind of asset. Open to every member. The drawer's card
 * and the details dialog share this one entry.
 */
export function useStorageUsage() {
  return useQuery({
    queryKey: assetKeys.storageUsage(),
    queryFn: ({ signal }) => getStorageUsage(signal),
  })
}

/**
 * What the organization's storage costs a month, by category (`billing-ui.md` 10.2, 10.3). Open to
 * every member, who read volumes; a billing manager also reads the amounts — on a platform that
 * sells credits — the monthly rates, and the five costliest assets. The details dialog and an old
 * version's cost line share it.
 */
export function useStorageCosts(options: { enabled?: MaybeRefOrGetter<boolean> } = {}) {
  return useQuery({
    queryKey: assetKeys.storageCosts(),
    queryFn: ({ signal }) => getStorageCosts(signal),
    enabled: computed(() => toValue(options.enabled) !== false),
  })
}
